import { useEffect, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  Building2,
  Eye,
  LogOut,
  Mail,
  MailOpen,
  Menu,
  MessageSquare,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { LOGOS } from "../images";
import PropertyManager from "./PropertyManager";
import ConversationManager from "./ConversationManager";
import MemberManager from "./MemberManager";
import Suivi from "./Suivi";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = { new: "Nouveau", read: "Lu", archived: "Archivé" };
const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "new", label: "Nouvelles" },
  { key: "read", label: "Lues" },
  { key: "archived", label: "Archivées" },
];

const TABS = [
  { key: "leads", label: "Demandes", icon: Mail },
  { key: "properties", label: "Propriétés", icon: Building2 },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "members", label: "Membres", icon: Users },
  { key: "suivi", label: "Suivi", icon: Eye },
];

const TAB_TITLES = {
  leads: "Demandes",
  properties: "Propriétés",
  messages: "Messages",
  members: "Membres",
  suivi: "Suivi",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("flen-admin") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [tab, setTab] = useState("leads");
  const [filter, setFilter] = useState("all");
  const [confirmId, setConfirmId] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [navOpen, setNavOpen] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("unauthorized");
      const data = await res.json();
      sessionStorage.setItem("flen-admin", data.token);
      setToken(data.token);
    } catch {
      setError("Mot de passe incorrect.");
    }
  };

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API}/api/leads`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setLeads(d.leads))
      .catch(() => setError("Chargement impossible."));
    Promise.all([
      fetch(`${API}/api/admin/properties`, { headers }).then((r) => (r.ok ? r.json() : { properties: [] })),
      fetch(`${API}/api/admin/members`, { headers }).then((r) => (r.ok ? r.json() : { members: [] })),
      fetch(`${API}/api/admin/conversations`, { headers }).then((r) => (r.ok ? r.json() : { conversations: [] })),
    ])
      .then(([p, m, c]) =>
        setOverview({ properties: p.properties || [], members: m.members || [], conversations: c.conversations || [] })
      )
      .catch(() => {});
  }, [token]);

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/leads/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("failed");
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      setError("Mise à jour impossible.");
    }
  };

  const deleteLead = async (id) => {
    try {
      const res = await fetch(`${API}/api/leads/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("failed");
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setConfirmId(null);
    } catch {
      setError("Suppression impossible.");
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      const res = await fetch(`${API}/api/leads/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: replyText }),
      });
      if (!res.ok) throw new Error("failed");
      setLeads((ls) =>
        ls.map((l) => (l.id === id ? { ...l, status: "read", replied_at: new Date().toISOString() } : l))
      );
      setReplyFor(null);
      setReplyText("");
    } catch {
      setError("Envoi de la réponse impossible.");
    } finally {
      setReplySending(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("flen-admin");
    setToken("");
    setLeads(null);
  };

  const counts = (leads || []).reduce(
    (acc, l) => {
      acc.all += 1;
      acc[l.status] = (acc[l.status] || 0) + 1;
      return acc;
    },
    { all: 0, new: 0, read: 0, archived: 0 }
  );
  const visible = (leads || []).filter((l) => filter === "all" || l.status === filter);

  const unreadMessages = overview ? overview.conversations.reduce((n, c) => n + (c.unread || 0), 0) : 0;
  const pendingMembers = overview ? overview.members.filter((m) => m.status === "pending").length : 0;
  const offMarketCount = overview ? overview.properties.filter((p) => p.off_market).length : 0;
  const publicCount = overview ? overview.properties.filter((p) => !p.off_market).length : 0;

  const badgeFor = (key) =>
    key === "leads" ? counts.new : key === "messages" ? unreadMessages : key === "members" ? pendingMembers : 0;

  if (!token)
    return (
      <div data-testid="admin-page" className="flex min-h-screen items-center justify-center bg-ink px-6 text-ivory">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm">
          <img src={LOGOS.horizontalNegativ} alt="FLEN Immobilier" className="h-9 w-auto" />
          <h1 className="mt-14 font-serif text-3xl font-medium">Administration</h1>
          <p className="mt-3 text-xs font-light text-ivory/45">Espace réservé à FLEN Immobilier.</p>
          <label htmlFor="admin-password" className="mb-2 mt-12 block text-[11px] uppercase tracking-[0.25em] text-ivory/50">
            Mot de passe
          </label>
          <input
            id="admin-password"
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border-0 border-b border-ivory/25 bg-transparent px-0 py-3 text-ivory outline-none transition-colors duration-300 focus:border-champagne"
          />
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-10 w-full border border-bronze px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-bronze"
          >
            Connexion
          </button>
        </form>
      </div>
    );

  const navItems = (onNavigate) =>
    TABS.map((tb) => {
      const Icon = tb.icon;
      const badge = badgeFor(tb.key);
      return (
        <button
          key={tb.key}
          data-testid={`admin-tab-${tb.key}`}
          onClick={() => {
            setTab(tb.key);
            if (onNavigate) onNavigate();
          }}
          className={`group flex items-center justify-between py-1 text-left ${
            tab === tb.key ? "text-champagne" : "text-ivory/40"
          }`}
        >
          <span className="flex items-center gap-4">
            <Icon size={15} strokeWidth={1.5} className={tab === tb.key ? "text-champagne" : "text-ivory/30 transition-colors duration-300 group-hover:text-ivory"} />
            <span className="text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 group-hover:text-ivory">
              {tb.label}
            </span>
          </span>
          {badge > 0 && (
            <span data-testid={`admin-tab-badge-${tb.key}`} className="border border-champagne/40 px-1.5 py-0.5 text-[9px] text-champagne">
              {badge}
            </span>
          )}
        </button>
      );
    });

  return (
    <div data-testid="admin-page" className="min-h-screen bg-ink font-sans text-ivory">
      {/* Sidebar (desktop) */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col justify-between border-r border-ivory/10 bg-ink px-8 py-10 md:flex" data-testid="admin-sidebar">
        <div>
          <img src={LOGOS.horizontalNegativ} alt="FLEN Immobilier" className="h-8 w-auto" />
          <p className="mt-3 text-[9px] uppercase tracking-[0.3em] text-ivory/30">Administration</p>
          <nav className="mt-16 flex flex-col gap-6" aria-label="Admin" data-testid="admin-tabs">
            {navItems()}
          </nav>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="group flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-ivory/40 transition-colors duration-300 hover:text-ivory"
        >
          <LogOut size={14} strokeWidth={1.5} className="transition-colors duration-300 group-hover:text-champagne" /> Déconnexion
        </button>
      </aside>

      {/* Mobile topbar */}
      <div className="flex items-center justify-between border-b border-ivory/10 px-6 py-5 md:hidden">
        <img src={LOGOS.horizontalNegativ} alt="FLEN Immobilier" className="h-7 w-auto" />
        <button data-testid="admin-mobile-menu-button" onClick={() => setNavOpen(true)} aria-label="Menu" className="text-ivory">
          <Menu size={24} strokeWidth={1.5} />
        </button>
      </div>
      {navOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-ink px-8 py-6 md:hidden" data-testid="admin-mobile-menu">
          <div className="flex items-center justify-between">
            <img src={LOGOS.horizontalNegativ} alt="FLEN Immobilier" className="h-7 w-auto" />
            <button onClick={() => setNavOpen(false)} aria-label="Fermer" className="text-ivory">
              <X size={26} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="mt-16 flex flex-col gap-7" aria-label="Admin mobile">
            {navItems(() => setNavOpen(false))}
          </nav>
          <button
            onClick={logout}
            className="mt-auto flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-ivory/50"
          >
            <LogOut size={14} strokeWidth={1.5} /> Déconnexion
          </button>
        </div>
      )}

      {/* Main */}
      <main className="px-6 py-10 md:ml-64 md:px-12 md:py-14 lg:px-16">
        <div className="max-w-6xl">
          <p className="text-[10px] uppercase tracking-[0.3em] text-bronze">FLEN · Administration</p>
          <h1 className="mt-3 font-serif text-3xl font-medium md:text-4xl" data-testid="admin-leads-title">
            {tab === "leads" ? `Demandes ${leads ? `(${leads.length})` : ""}` : TAB_TITLES[tab]}
          </h1>

          {/* Metrics overview */}
          <div className="mb-16 mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6" data-testid="admin-metrics">
            {[
              { label: "Nouvelles demandes", value: counts.new, sub: `${counts.all} au total`, testid: "metric-new-leads" },
              { label: "Messages non lus", value: unreadMessages, sub: `${overview ? overview.conversations.length : 0} conversations`, testid: "metric-unread-messages" },
              { label: "Membres en attente", value: pendingMembers, sub: `${overview ? overview.members.length : 0} membres`, testid: "metric-pending-members" },
              { label: "Off-market", value: offMarketCount, sub: `${publicCount} publiques`, testid: "metric-offmarket" },
            ].map((m) => (
              <div key={m.testid} data-testid={m.testid} className="flex flex-col justify-between gap-6 border border-ivory/10 p-6 transition-colors duration-500 hover:border-ivory/25 lg:p-7">
                <p className="text-[10px] uppercase tracking-[0.25em] text-champagne">{m.label}</p>
                <div>
                  <p className="font-serif text-4xl tracking-tight lg:text-5xl">{m.value}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ivory/35">{m.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {error && <p data-testid="admin-error" className="mb-8 border border-red-400/30 px-5 py-4 text-sm text-red-300">{error}</p>}

          {tab === "leads" && (
            <>
              <div className="flex flex-wrap gap-2" data-testid="admin-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`admin-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`border px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                      filter === f.key
                        ? "border-bronze bg-bronze text-ivory"
                        : "border-ivory/20 text-ivory/50 hover:border-ivory/40 hover:text-ivory"
                    }`}
                  >
                    {f.label} ({counts[f.key] || 0})
                  </button>
                ))}
              </div>

              {!leads ? (
                <p className="mt-10 text-ivory/50">Chargement…</p>
              ) : visible.length === 0 ? (
                <div className="mt-10 border border-ivory/10 px-12 py-24 text-center" data-testid="admin-leads-empty">
                  <p className="font-serif text-xl text-ivory/60">Aucune demande</p>
                  <p className="mt-2 text-xs font-light text-ivory/35">Il n&apos;y a aucune demande dans cette catégorie.</p>
                </div>
              ) : (
                <div className="mt-8 divide-y divide-ivory/10 border-y border-ivory/10" data-testid="admin-leads-list">
                  {visible.map((l) => (
                    <div key={l.id} data-testid={`admin-lead-${l.id}`} className="grid gap-3 px-2 py-8 transition-colors duration-300 hover:bg-ivory/[0.02] md:grid-cols-[200px_1fr_150px] md:gap-8">
                      <div>
                        <p className="text-sm text-ivory">
                          {l.first_name} {l.last_name}
                        </p>
                        <a href={`mailto:${l.email}`} className="text-xs text-champagne">{l.email}</a>
                        {l.phone && <p className="text-xs text-ivory/50">{l.phone}</p>}
                        {l.property_ref && (
                          <p data-testid={`admin-lead-property-${l.id}`} className="mt-1 text-xs text-champagne/80">
                            {l.property_ref}
                          </p>
                        )}
                        <span
                          data-testid={`admin-lead-status-${l.id}`}
                          className={`mt-2 inline-block border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                            l.status === "new"
                              ? "border-champagne/50 text-champagne"
                              : l.status === "read"
                                ? "border-ivory/30 text-ivory/60"
                                : "border-ivory/15 text-ivory/35"
                          }`}
                        >
                          {STATUS_LABELS[l.status] || l.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-light leading-relaxed text-ivory/70">{l.message}</p>
                        {l.replied_at && (
                          <p data-testid={`admin-lead-replied-${l.id}`} className="mt-2 text-[10px] uppercase tracking-[0.2em] text-champagne">
                            Répondu le {new Date(l.replied_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })}
                          </p>
                        )}
                        {replyFor === l.id ? (
                          <div className="mt-4" data-testid={`admin-lead-reply-form-${l.id}`}>
                            <textarea
                              rows={4}
                              data-testid={`admin-lead-reply-text-${l.id}`}
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Votre réponse…"
                              className="w-full resize-none border border-ivory/20 bg-transparent p-3 text-sm font-light text-ivory outline-none transition-colors duration-300 placeholder:text-ivory/30 focus:border-champagne"
                            />
                            <div className="mt-3 flex gap-2">
                              <button
                                data-testid={`admin-lead-reply-send-${l.id}`}
                                onClick={() => sendReply(l.id)}
                                disabled={replySending}
                                className="flex items-center gap-1.5 border border-bronze px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-ivory transition-colors duration-300 hover:bg-bronze disabled:opacity-50"
                              >
                                <Send size={12} /> {replySending ? "Envoi…" : "Envoyer"}
                              </button>
                              <button
                                data-testid={`admin-lead-reply-cancel-${l.id}`}
                                onClick={() => setReplyFor(null)}
                                className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-ivory/40 transition-colors duration-300 hover:text-ivory"
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            data-testid={`admin-lead-reply-${l.id}`}
                            onClick={() => {
                              setReplyFor(l.id);
                              setReplyText("");
                            }}
                            className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-ivory/40 transition-colors duration-300 hover:text-champagne"
                          >
                            <Send size={12} /> Répondre
                          </button>
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-2 text-xs text-ivory/45 md:items-end">
                        <p className="uppercase tracking-[0.2em] text-bronze">{l.project_type}</p>
                        <p>{new Date(l.created_at).toLocaleString("fr-CH", { dateStyle: "medium", timeStyle: "short" })}</p>
                        <div className="mt-1 flex gap-2">
                          {l.status === "new" && (
                            <button
                              data-testid={`admin-lead-mark-read-${l.id}`}
                              onClick={() => updateStatus(l.id, "read")}
                              className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-bronze hover:text-ivory"
                            >
                              <MailOpen size={12} /> Marquer comme lu
                            </button>
                          )}
                          {l.status !== "archived" ? (
                            <button
                              data-testid={`admin-lead-archive-${l.id}`}
                              onClick={() => updateStatus(l.id, "archived")}
                              className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-bronze hover:text-ivory"
                            >
                              <Archive size={12} /> Archiver
                            </button>
                          ) : (
                            <button
                              data-testid={`admin-lead-restore-${l.id}`}
                              onClick={() => updateStatus(l.id, "read")}
                              className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-bronze hover:text-ivory"
                            >
                              <ArchiveRestore size={12} /> Restaurer
                            </button>
                          )}
                          {confirmId === l.id ? (
                            <span className="flex items-center gap-2" data-testid={`admin-lead-delete-confirm-${l.id}`}>
                              <button
                                data-testid={`admin-lead-delete-yes-${l.id}`}
                                onClick={() => deleteLead(l.id)}
                                className="border border-red-400/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-red-300 transition-colors duration-300 hover:bg-red-400/10"
                              >
                                Confirmer
                              </button>
                              <button
                                data-testid={`admin-lead-delete-no-${l.id}`}
                                onClick={() => setConfirmId(null)}
                                className="px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/40 transition-colors duration-300 hover:text-ivory"
                              >
                                Annuler
                              </button>
                            </span>
                          ) : (
                            <button
                              data-testid={`admin-lead-delete-${l.id}`}
                              onClick={() => setConfirmId(l.id)}
                              className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-red-400/60 hover:text-red-300"
                            >
                              <Trash2 size={12} /> Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {tab === "properties" && <PropertyManager token={token} setError={setError} />}
          {tab === "messages" && <ConversationManager token={token} setError={setError} />}
          {tab === "members" && <MemberManager token={token} setError={setError} />}
          {tab === "suivi" && <Suivi token={token} setError={setError} />}
        </div>
      </main>
    </div>
  );
}
